import { generatePath, NavLink } from "react-router-dom";

import Card from "~/components/Card/Card";
import DetailsListDivider from "~/components/DetailsList/components/DetailsListDivider";
import DetailsListItem from "~/components/DetailsList/components/DetailsListItem";
import Headline from "~/components/Headline/Headline";
import { COMPANY_DETAILS_PATH, QUOTA_DETAILS_PATH_WITH_ORIGIN } from "~/constants/appRoutes.ts";
import InvoiceDetailsLink from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceDetailsLink.tsx";
import { getPhasePath } from "~/modules/project/utils/projectDetailsViewUtils.ts";
import type { Quota, QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import { validQuotaDetailsNavigatedFromOrigins } from "~/modules/quota/components/QuotaDetailsView/quotaDetailsUtils.ts";

type Props = {
	invoiceId: string;
	quota: QuotaWithRelations;
};

function getInvoiceQuotaCardTitle(quota: Quota) {
	return quota.type === "advance" ? "Abrechnungskontingent" : "Zugehöriges Abrechnungskontingent";
}

// The quota of an invoice: the advance quota of an advance invoice, or the invoice-backed quota that an
// attached invoice backs. It lists the consumer orders of the quota.
export const InvoiceQuotaDetailsSection = ({ invoiceId, quota }: Props) => {
	return <Card title={getInvoiceQuotaCardTitle(quota)}
				 padding="none">
		<div className="grid grid-cols-[auto_1fr] gap-4 p-4">
			<DetailsListItem label="Titel">
				<InvoiceDetailsLink
					to={QUOTA_DETAILS_PATH_WITH_ORIGIN(quota.companyId,
						quota.id,
						validQuotaDetailsNavigatedFromOrigins.INVOICE,
						{ invoiceId })}
				>
					{quota.title}
				</InvoiceDetailsLink>
			</DetailsListItem>
			<DetailsListDivider />
			<DetailsListItem label="Kunde">
				<InvoiceDetailsLink
					to={generatePath(COMPANY_DETAILS_PATH, { companyId: quota.companyId })}
				>
					{quota.company.displayName}
				</InvoiceDetailsLink>
			</DetailsListItem>
			<DetailsListDivider />
			<DetailsListItem label="Bestellungen">
				<div className="space-y-2">
					{quota.consumers.length === 0 &&
						<div className="text-sm text-gray-500">Keine Bestellungen zugewiesen.</div>}
					{quota.consumers.map((consumer) => {
						// The quota response with relations embeds the order of each consumer (BE1).
						const order = consumer.order;
						if (!order) return null;
						return <NavLink key={consumer.id}
										to={getPhasePath(order.projectId, order.projectPhase.phaseNumber)}
										className="group flex grow items-center justify-between rounded-lg border border-gray-200 px-4 py-2 hover:bg-gray-100">
							<div>
								<Headline type="h5"
										  color="muted">{order.title}</Headline>
								<div className="text-sm text-gray-500">{`${order.project.titleWithNumber} · ${order.projectPhase.title}`}</div>
							</div>
						</NavLink>;
					})}
				</div>
			</DetailsListItem>
		</div>
	</Card>;
};

type SummaryProps = {
	quota: Quota;
};

// The quota of an invoice for a user without quota access. It shows the quota from the invoice data only,
// without links, because the quota routes refuse that user.
export const InvoiceQuotaSummarySection = ({ quota }: SummaryProps) => {
	return <Card title={getInvoiceQuotaCardTitle(quota)}
				 padding="none">
		<div className="grid grid-cols-[auto_1fr] gap-4 p-4">
			<DetailsListItem label="Titel">{quota.title}</DetailsListItem>
		</div>
	</Card>;
};
